import { AudioLines, Eye, EyeOff, Plug, Sparkles, type LucideIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useApp } from "@/lib/AppContext";
import { ApiError, type ProviderCredential, type ProviderSettings as Payload } from "@/lib/api";
import { cn } from "@/lib/cn";

/**
 * UI-14 — Settings → Cloud providers.
 *
 * The companion to Engines & models: the services a cloud placement calls, and the
 * keys they need. Two of them, which is the two the Engines table in FEATURES.md points
 * at. Hugging Face is deliberately not here — it is where the worker fetches weights
 * from rather than a service an engine calls, and its token belongs on the machine that
 * downloads them (Settings → Worker, M3.4).
 *
 * The one property worth stating plainly, because it shapes every control on this
 * screen: **a stored key is never sent back**. Show/hide is therefore about the draft
 * being typed, which is the only moment the browser ever holds one; a saved key is a
 * presence, four characters and whatever the last test found. The handoff says the same
 * thing in its own helper line — "stored on the Server and never sent to the browser
 * again" — so there is nothing to reveal and no reveal button.
 */

const ICONS: Record<string, LucideIcon> = {
  anthropic: Sparkles,
  elevenlabs: AudioLines,
};

export function ProviderSettings({ summary }: { summary: string }) {
  const { api } = useApp();

  const [data, setData] = useState<Payload | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.getProviders());
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "The providers could not be loaded.");
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  function patch(stored: ProviderCredential) {
    setData((current) =>
      current === null
        ? current
        : {
            ...current,
            providers: current.providers.map((p) => (p.provider === stored.provider ? stored : p)),
          },
    );
  }

  async function save(provider: string, key: string) {
    setBusy(provider);
    setProblem(null);

    try {
      patch(await api.saveProviderKey(provider, key));
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "That key could not be saved.");
    } finally {
      setBusy(null);
    }
  }

  async function remove(provider: string) {
    setBusy(provider);
    setProblem(null);

    try {
      await api.removeProviderKey(provider);
      await load();
    } catch (cause: unknown) {
      setProblem(cause instanceof ApiError ? cause.message : "That key could not be removed.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex max-w-[860px] flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header>
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          Cloud providers
        </h1>
        <p className="mt-1 text-[length:var(--text-md)] text-[var(--text-secondary)]">{summary}</p>
      </header>

      {problem && (
        <p
          role="alert"
          className="rounded-[var(--radius-lg)] border border-[var(--warning)] bg-[var(--surface-glass)] px-[var(--space-4)] py-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--text-primary)]"
        >
          {problem}
        </p>
      )}

      {data === null ? (
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>
      ) : (
        <>
          {!data.keyStorage.available && (
            <p
              role="status"
              className="rounded-[var(--radius-lg)] border border-[var(--warning)] bg-[var(--surface-glass)] px-[var(--space-4)] py-[var(--space-3)] text-[length:var(--text-sm)] text-[var(--text-primary)]"
            >
              {data.keyStorage.problem}
            </p>
          )}

          {data.catalogue.map((definition) => {
            const credential = data.providers.find((p) => p.provider === definition.provider);
            if (!credential) return null;

            return (
              <ProviderCard
                key={definition.provider}
                definition={definition}
                credential={credential}
                usedBy={data.usedBy[definition.provider] ?? []}
                serves={data.serves[definition.provider] ?? []}
                storable={data.keyStorage.available}
                busy={busy === definition.provider}
                onSave={(key) => void save(definition.provider, key)}
                onRemove={() => void remove(definition.provider)}
              />
            );
          })}
        </>
      )}
    </div>
  );
}

function ProviderCard({
  definition,
  credential,
  usedBy,
  serves,
  storable,
  busy,
  onSave,
  onRemove,
}: {
  definition: Payload["catalogue"][number];
  credential: ProviderCredential;
  usedBy: string[];
  serves: string[];
  storable: boolean;
  busy: boolean;
  onSave: (key: string) => void;
  onRemove: () => void;
}) {
  const [draft, setDraft] = useState("");
  const [visible, setVisible] = useState(false);

  const Icon = ICONS[definition.provider] ?? Plug;

  return (
    <section className="flex flex-col gap-[var(--space-4)] rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-4)] backdrop-blur-[var(--blur-sm)]">
      <div className="flex items-center gap-[var(--space-3)]">
        <Icon size={16} strokeWidth={2} className="shrink-0 text-[var(--text-muted)]" />

        <div className="min-w-0 flex-1">
          <h2 className="text-[length:var(--text-lg)] font-bold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
            {definition.name}
          </h2>
          <p className="mt-[2px] text-[length:var(--text-xs)] text-[var(--text-muted)]">
            {definition.what}
          </p>
        </div>

        <ConnectionPill credential={credential} />
      </div>

      {credential.connected ? (
        <div className="flex flex-col gap-[6px]">
          <div className="flex items-center gap-[8px]">
            <span className="flex-1 truncate rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-2)] px-[var(--space-3)] py-[8px] font-[family-name:var(--font-mono)] text-[length:var(--text-xs)] text-[var(--text-secondary)]">
              {credential.hint ? `••••••••${credential.hint}` : "••••••••"}
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={onRemove}
              className="rounded-[var(--radius-lg)] border border-[var(--border-default)] px-[var(--space-3)] py-[var(--space-1)] text-[length:var(--text-xs)] font-semibold text-[var(--text-primary)] disabled:opacity-50"
            >
              Remove
            </button>
          </div>
          <span className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">
            The key is on the Server and is never sent back to this browser. Replacing it means
            entering a new one.
          </span>
        </div>
      ) : (
        <form
          className="flex flex-col gap-[6px]"
          onSubmit={(event) => {
            event.preventDefault();
            if (draft.trim().length > 0) onSave(draft.trim());
          }}
        >
          <div className="flex items-center justify-between">
            <label
              htmlFor={`key-${definition.provider}`}
              className="text-[length:var(--text-sm)] font-medium text-[var(--text-primary)]"
            >
              {definition.keyLabel}
            </label>
            <span className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">
              {definition.docs}
            </span>
          </div>

          <div className="flex items-center gap-[8px]">
            <input
              id={`key-${definition.provider}`}
              type={visible ? "text" : "password"}
              value={draft}
              autoComplete="off"
              spellCheck={false}
              disabled={busy || !storable}
              placeholder={definition.keyPlaceholder}
              onChange={(event) => setDraft(event.target.value)}
              className="min-w-0 flex-1 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-1)] px-[var(--space-3)] py-[8px] font-[family-name:var(--font-mono)] text-[length:var(--text-xs)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] disabled:opacity-60"
            />

            <button
              type="button"
              onClick={() => setVisible((current) => !current)}
              aria-label={visible ? "Hide the key while typing" : "Show the key while typing"}
              className="rounded-[var(--radius-lg)] border border-[var(--border-default)] p-[8px] text-[var(--text-secondary)]"
            >
              {visible ? <EyeOff size={14} strokeWidth={2} /> : <Eye size={14} strokeWidth={2} />}
            </button>

            <button
              type="submit"
              disabled={busy || !storable || draft.trim().length === 0}
              className="shrink-0 rounded-[var(--radius-lg)] bg-[var(--primary)] px-[var(--space-4)] py-[8px] text-[length:var(--text-xs)] font-semibold text-[var(--primary-foreground)] disabled:opacity-50"
            >
              {busy ? "Testing…" : "Save and test"}
            </button>
          </div>

          <span className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">
            The key is stored on the Server, encrypted, and never sent to this browser again.
          </span>
        </form>
      )}

      <p className="border-t border-[var(--border-subtle)] pt-[10px] text-[length:var(--text-xs)] text-[var(--text-muted)]">
        {usedBy.length > 0
          ? `Used by ${usedBy.join(", ")}.`
          : serves.length > 0
            ? `Nothing is placed here yet. It would run ${serves.join(", ")}.`
            : "Not used by any engine right now."}
      </p>
    </section>
  );
}

/**
 * What the last test found, kept rather than re-run on every render — which is why
 * `provider_credentials` stores it.
 *
 * A failed test is reported and nothing more. An unreachable provider does not stop a
 * key being saved and does not stop the pipeline: the service may be down, the key may
 * work tomorrow, and throwing away what the owner typed would be the worse answer.
 */
function ConnectionPill({ credential }: { credential: ProviderCredential }) {
  const label = !credential.connected
    ? "Not connected"
    : credential.lastTestError
      ? "Key stored, test failed"
      : credential.lastTestLatencyMs !== null
        ? `Connected · ${credential.lastTestLatencyMs} ms`
        : "Key stored";

  const tone = !credential.connected
    ? "var(--text-muted)"
    : credential.lastTestError
      ? "var(--warning)"
      : "var(--success)";

  return (
    <span className="flex flex-col items-end gap-[2px]">
      <span
        className={cn(
          "shrink-0 rounded-[var(--radius-full)] bg-[var(--surface-2)] px-[var(--space-2)] py-[2px]",
          "text-[length:var(--text-xs)] font-semibold",
        )}
        style={{ color: tone }}
      >
        {label}
      </span>
      {credential.lastTestError && (
        <span className="max-w-[280px] text-right text-[length:var(--text-2xs)] text-[var(--text-muted)]">
          {credential.lastTestError}
        </span>
      )}
    </span>
  );
}
